import { useEffect, useMemo, useState } from 'react';
import type { ComponentEnhancer } from 'recompose';
import { withRelay, NetworkOptions } from '@mindtickle/ui-shell-core/Relay';
import { useUserAuth } from '~/components/Auth';
import authMiddleware from '../middlewares/auth';

import createSubscribeFn from '../middlewares/subscription';
import { EnvironmentConfig } from 'relay-runtime/lib/store/RelayModernEnvironment';

const withCustomRelay = <P extends {}>({
  graphqlEndpoint,
  enableSubscription,
  environmentOptions,
}: {
  graphqlEndpoint: string;
  enableSubscription: boolean;
  environmentOptions?: EnvironmentConfig;
}) => {
  const enhancer = (Component: React.ComponentType<P>) => (props: P) => {
    const [subscriptionClient, setSubscriptionClient] = useState<{
      closeClientConnection: (isForced?: boolean) => void;
    } | null>(null);

    const userAuth = useUserAuth();
    let options = useMemo(() => {
      const networkOptions = {} as NetworkOptions;
      if (enableSubscription) {
        let subscriptionEndpoint = graphqlEndpoint.split('//')[1];
        if (!subscriptionEndpoint) {
          subscriptionEndpoint = window.location.host + graphqlEndpoint;
        }
        const { subscribe, closeClientConnection } = createSubscribeFn(
          userAuth.sessionId,
          'wss://'.concat(subscriptionEndpoint)
        );
        networkOptions['subscribeFn'] = subscribe;
        setSubscriptionClient({ closeClientConnection });
      }
      return networkOptions;
    }, [userAuth.sessionId]);

    useEffect(
      () => () => {
        subscriptionClient && subscriptionClient.closeClientConnection(true);
      },
      [] // eslint-disable-line
    );

    const WrappedComponent = useMemo(
      () =>
        withRelay<P>({
          graphqlEndpoint,
          middlewares: [authMiddleware(userAuth.sessionId)],
          options,
          environmentOptions,
        })(Component),
      [userAuth.sessionId, options]
    );

    return <WrappedComponent {...props} />;
  };
  return (enhancer as unknown) as ComponentEnhancer<P, P>;
};

export default withCustomRelay;
